import { useEffect, useRef, useState } from 'react';
import type { GameActions } from '../app/connection';
import { useRemaining } from '../app/clock';
import { sfx } from '../audio/sfx';
import type { GameState } from '../app/state';
import { playerName, seatIndex } from '../app/state';
import { Button } from '../ui/Button';
import { Countdown } from '../ui/Countdown';
import { Dice } from '../ui/Dice';
import { Money } from '../ui/Money';
import { Seat } from '../ui/Seat';
import { Standings } from '../ui/Standings';
import { cx } from '../ui/cx';
import { miniGameLabel, money, themeLabel } from '../ui/format';
import s from './screens.module.scss';

/**
 * La mise.
 *
 * C'est le seul moment ou on decide sans rien voir : on connait le theme et le
 * type de question, pas la question. Tout l'ecran sert cette asymetrie — le
 * teaser est court, la fourchette est visible, et le plafond qui monte de
 * manche en manche est affiche, parce que c'est lui le mecanisme de remontee.
 *
 * Les pouvoirs se posent ICI, avec la mise, et pas apres. Active apres coup,
 * « je double mes gains » ne couterait rien ; declare avant, c'est un pari.
 */
export interface BettingScreenProps {
  state: GameState;
  actions: GameActions;
}

/** Envoi differe : un curseur qu'on fait glisser ne doit pas inonder la socket. */
const COMMIT_MS = 220;

export function BettingScreen({ state, actions }: BettingScreenProps) {
  const betting = state.betting;
  const remaining = useRemaining(state.phaseEndsAt);
  const [stake, setStake] = useState(betting?.stake ?? 0);
  const [targeting, setTargeting] = useState<string | null>(null);

  // Le serveur fait foi : s'il corrige la mise, le curseur suit.
  useEffect(() => {
    if (betting) setStake(betting.stake);
  }, [betting?.roundNumber, betting?.stake]);

  useEffect(() => {
    if (!betting || stake === betting.stake) return;
    const timer = window.setTimeout(() => actions.setStake(stake), COMMIT_MS);
    return () => window.clearTimeout(timer);
  }, [stake, betting, actions]);

  // Un pouvoir vient d'etre pose — le sien ou celui d'un adversaire. Sa CIBLE
  // s'illumine brievement dans le classement : sans ca, « Lucas pose Siphon
  // sur Ana » n'est qu'une ligne de texte, jamais un evenement qu'on VOIT.
  const declaredSoFar = betting?.declared ?? [];
  const lastDeclaredCount = useRef(declaredSoFar.length);
  const [flash, setFlash] = useState<{ playerId: string; kind: 'bonus' | 'malus' } | null>(null);

  useEffect(() => {
    const grew = declaredSoFar.length > lastDeclaredCount.current;
    lastDeclaredCount.current = declaredSoFar.length;
    if (!grew) return undefined;

    sfx.power();
    const entry = declaredSoFar.at(-1);
    const kind = state.catalogue.find((p) => p.id === entry?.powerId)?.kind;
    if (!entry?.targetId || !kind) return undefined;

    setFlash({ playerId: entry.targetId, kind });
    const timer = window.setTimeout(() => setFlash(null), 900);
    return () => window.clearTimeout(timer);
  }, [declaredSoFar, state.catalogue]);

  if (!betting) return null;

  const { range, teaser, powers, declared } = betting;
  const allIn = stake >= range.max;

  return (
    <main className={s.betting}>
      <header className={s.bettingHeader}>
        <span className={s.roundTag}>
          Manche {betting.roundNumber} / {betting.totalRounds}
        </span>
        <Countdown remainingMs={remaining} totalMs={state.phaseTotalMs} label="Mises" />
      </header>

      <section className={s.teaser}>
        <span className={s.teaserTheme}>{themeLabel(teaser.theme)}</span>
        <h1 className={s.teaserPrompt}>{teaser.prompt}</h1>
        <span className={s.teaserGame}>{miniGameLabel(teaser.miniGame)}</span>
      </section>

      <div className={s.bettingColumns}>
        <section className={s.bettingPanel}>
          <h2 className={s.panelTitle}>Ta mise</h2>

          <div className={s.stakeValue}>
            <Money value={stake} />
            <span className={cx(s.stakeAllIn, allIn && s.stakeAllInOn)}>tapis</span>
          </div>

          <input
            className={s.stakeRange}
            type="range"
            min={range.min}
            max={range.max}
            step={10}
            value={stake}
            onChange={(event) => setStake(Number(event.target.value))}
            aria-label="Montant de ta mise"
          />

          <div className={s.stakeBounds}>
            <span>min {money(range.min)}</span>
            <span>max {money(range.max)}</span>
          </div>

          <div className={s.stakeShortcuts}>
            <Button tone="ghost" onClick={() => setStake(range.min)}>
              minimum
            </Button>
            <Button
              tone="ghost"
              onClick={() =>
                setStake(Math.min(range.max, Math.max(range.min, Math.round(betting.bankroll / 2))))
              }
            >
              moitie
            </Button>
            <Button tone="action" onClick={() => setStake(range.max)}>
              tapis
            </Button>
          </div>

          <p className={s.bettingNote}>
            Cagnotte <Money value={betting.bankroll} sound />. Se tromper coute la mise ; ne pas repondre
            ne coute rien.
          </p>
        </section>

        <section className={s.bettingPanel}>
          <h2 className={s.panelTitle}>Pouvoirs</h2>

          <ul className={s.slotList}>
            {powers.map((slot) => {
              const mine = declared.some(
                (entry) => entry.ownerId === state.you && entry.powerId === slot.id,
              );
              const open = targeting === slot.id;
              return (
                <li key={slot.id}>
                  <button
                    type="button"
                    className={cx(s.slot, mine && s.slotUsed, open && s.slotOpen)}
                    disabled={!slot.usable || mine}
                    onClick={() => {
                      if (slot.targeting === 'self') actions.usePower(slot.id);
                      else setTargeting(open ? null : slot.id);
                    }}
                  >
                    <strong className={s.slotLabel}>{slot.label}</strong>
                    <span className={s.slotMeta}>
                      {slot.usesLeft === null ? 'illimite' : `${slot.usesLeft} restants`}
                      {slot.cooldownLeft > 0 ? ` · recharge ${slot.cooldownLeft}` : ''}
                      {mine ? ' · pose' : ''}
                    </span>
                  </button>

                  {/* Le choix de cible n'apparait qu'a la demande : huit joueurs
                      affiches sous chaque pouvoir noieraient l'ecran. */}
                  {open ? (
                    <ul className={s.targetList}>
                      {state.players
                        .filter((player) => player.id !== state.you)
                        .map((player) => (
                          <li key={player.id}>
                            <button
                              type="button"
                              className={s.target}
                              onClick={() => {
                                actions.usePower(slot.id, player.id);
                                setTargeting(null);
                              }}
                            >
                              <Seat
                                name={player.name}
                                seatIndex={seatIndex(state, player.id)}
                                bot={player.bot}
                              />
                            </button>
                          </li>
                        ))}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>

          {declared.length > 0 ? (
            <ul className={s.declaredList}>
              {declared.map((entry, index) => (
                <li key={`${entry.ownerId}-${entry.powerId}-${index}`} className={s.declaredRow}>
                  <strong>{playerName(state, entry.ownerId)}</strong> pose{' '}
                  <em>{state.catalogue.find((p) => p.id === entry.powerId)?.label ?? entry.powerId}</em>
                  {entry.targetId && entry.targetId !== entry.ownerId ? (
                    <> sur <strong>{playerName(state, entry.targetId)}</strong></>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className={s.bettingNote}>Personne n'a encore rien pose.</p>
          )}
        </section>

        <aside className={s.bettingRail}>
          {state.income ? <Dice state={state} rolls={state.income} /> : null}
          <Standings
            state={state}
            standings={betting.standings}
            title="Classement"
            flashPlayerId={flash?.playerId ?? null}
            flashKind={flash?.kind}
          />
        </aside>
      </div>
    </main>
  );
}
